import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useUploadContextFactory } from '../upload-contexts/use-upload-context-factory';
import { findClosestMatch } from '../../../../../lib/search-utils';
import GoBackButton from './go-back-button';
import { cn } from '@/lib/utils';

const ColumnMapper = ({ contextType }) => {
  const {
    headers,
    fileData,
    propertyOptions,
    mapFileHeadersToUserProperties,
    goBack,
    mandatoryFields = [],
  } = useUploadContextFactory({ contextType });

  const doInitialMapping = () => {
    const mappedColumns = {};
    headers.forEach(header => {
      const closestMatch = findClosestMatch(header, Object.keys(propertyOptions));
      mappedColumns[header] = {
        value: closestMatch,
        collapsed: false,
        mandatory: mandatoryFields.includes(closestMatch),
      };
    });
    return mappedColumns;
  };

  const [columnMapper, setColumnMap] = useState(doInitialMapping);

  const updateMapping = (column, value) => {
    setColumnMap(prev => ({
      ...prev,
      [column]: { value, collapsed: false, mandatory: mandatoryFields.includes(value) },
    }));
  };

  const expandMapper = column => {
    setColumnMap(prev => ({
      ...prev,
      [column]: { value: prev[column].value, collapsed: false },
    }));
  };

  const createColumnMappings = () => {
    return Object.entries(columnMapper).reduce((acc, [key, { value }]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const isValidateDisabled = Object.values(columnMapper).some(({ collapsed }) => !collapsed);

  return (
    <div>
      <GoBackButton onClick={goBack} />
      {fileData && (
        <div className="border border-solid p-6 rounded-md flex flex-col gap-6">
          {headers.map((col, index) => (
            <div
              key={col}
              className={cn('border border-solid p-6 rounded-md', {
                'bg-green-200': columnMapper[col].value && columnMapper[col].collapsed,
                'bg-slate-200': !columnMapper[col].value && columnMapper[col].collapsed,
              })}>
              {columnMapper[col].collapsed ? (
                <CollapsedMapper col={col} expandColumn={expandMapper} />
              ) : (
                <ExpandedMapper
                  col={col}
                  updateMapping={updateMapping}
                  propertyOptions={propertyOptions}
                  localMappings={columnMapper}
                  sampleData={fileData}
                  columnIndex={index}
                  setColumnMap={setColumnMap}
                />
              )}
            </div>
          ))}
          <Footer
            onClick={() => {
              mapFileHeadersToUserProperties(createColumnMappings());
            }}
            disabled={isValidateDisabled}
          />
        </div>
      )}
    </div>
  );
};

const ExpandedMapper = ({
  col,
  updateMapping,
  propertyOptions,
  localMappings,
  sampleData,
  columnIndex,
  setColumnMap,
}) => {
  const confirmMappingAndCollapse = column => {
    setColumnMap(prev => ({
      ...prev,
      [column]: { value: prev[column].value, collapsed: true },
    }));
  };

  const removeMappingAndCollapse = column => {
    setColumnMap(prev => ({
      ...prev,
      [column]: { value: undefined, collapsed: true },
    }));
  };
  return (
    <div className="flex gap-6">
      <div id={col} className="w-96 flex flex-col gap-4">
        <ColumnSelector
          col={col}
          updateMapping={updateMapping}
          propertyOptions={propertyOptions}
          localMappings={localMappings}
        />
        <SampleData data={sampleData} columnIndex={columnIndex} />
      </div>
      <ConfirmationButtons
        disabled={!localMappings[col].value}
        confirm={() => confirmMappingAndCollapse(col)}
        remove={() => removeMappingAndCollapse(col)}
        mandatory={localMappings[col].mandatory}
      />
    </div>
  );
};

const ColumnSelector = ({ col, updateMapping, propertyOptions, localMappings }) => (
  <div className="flex gap-6 items-center">
    <Label className="font-bold">{col}</Label>
    <Select
      value={localMappings[col].value}
      onValueChange={value => updateMapping(col, value)}
      className="w-48">
      <SelectTrigger className="rounded-none">
        <SelectValue placeholder="Select a property" />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(propertyOptions).map(([propertyKey, propertyValue]) => (
          <SelectItem key={propertyKey} value={propertyKey}>
            {propertyValue}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const SampleData = ({ data, columnIndex }) => (
  <Table>
    <TableBody>
      {data.slice(0, 3).map((row, rowIndex) => (
        <TableRow
          key={rowIndex}
          className="cursor-default pointer-events-none odd:bg-white even:bg-slate-50">
          <TableCell className="w-8 border border-solid">{rowIndex}</TableCell>
          <TableCell className="border border-solid">{row[columnIndex] || ''}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const ConfirmationButtons = ({ disabled, confirm, remove, mandatory = false }) => (
  <div className="flex-1 flex justify-end gap-2 items-end">
    <Button className="bg-green-500" onClick={confirm} disabled={disabled}>
      Confirm Mapping
    </Button>
    <Button disabled={mandatory} onClick={remove}>
      Remove Column
    </Button>
  </div>
);

const CollapsedMapper = ({ col, expandColumn }) => (
  <div className="flex justify-between items-center">
    <Label className="font-bold">{col}</Label>
    <Button onClick={() => expandColumn(col)}>Edit</Button>
  </div>
);

const Footer = ({ onClick, disabled }) => (
  <div className="flex flex-col gap-2">
    <Button onClick={onClick} className="w-48" disabled={disabled}>
      Validate
    </Button>
    {disabled && (
      <Label className="text-red-500">
        You must confirm/remove all columns before moving to the next step
      </Label>
    )}
  </div>
);

export default ColumnMapper;
